import React, { useEffect, Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import Navbar from '@/components/Navbar';
import MobileNav from '@/components/MobileNav';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import Footer from '@/components/Footer';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Analytics } from '@vercel/analytics/react';
import SynapseWidget from '@/components/SynapseWidget';
import { Loader2 } from 'lucide-react';

const HomePage = lazy(() => import('@/pages/HomePage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const EventsPage = lazy(() => import('@/pages/EventsPage'));
const ResourcesPage = lazy(() => import('@/pages/ResourcesPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const MedicalDisclaimerPage = lazy(() => import('@/pages/MedicalDisclaimerPage'));
const MessagesPage = lazy(() => import('@/pages/MessagesPage'));
const DailyNewsPage = lazy(() => import('@/pages/DailyNewsPage'));
const SectionPage = lazy(() => import('@/pages/SectionPage'));
const DentalClinicPage = lazy(() => import('@/pages/DentalClinicPage'));
const StudyHubPage = lazy(() => import('@/pages/StudyHubPage'));
const CrushesPage = lazy(() => import('@/pages/CrushesPage'));
const StudyBuddyPage = lazy(() => import('@/pages/StudyBuddyPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const WriteForUsPage = lazy(() => import('@/pages/WriteForUsPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));

function ThemeWrapper({ children }) {
  const [theme, setTheme] = useLocalStorage('umf_theme', 'light');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  return children({ theme, toggleTheme });
}

function App() {
  return (
    <ThemeWrapper>
      {({ theme, toggleTheme }) => (
        <AuthProvider>
          <BrowserRouter>
            <div className="min-h-screen bg-background text-foreground font-body">
              <Navbar theme={theme} toggleTheme={toggleTheme} />
              <main>
                <ErrorBoundary>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>}>
                  <Routes>
                    <Route path="/" element={<ErrorBoundary><HomePage /></ErrorBoundary>} />
                    <Route path="/profile" element={<ErrorBoundary><ProfilePage /></ErrorBoundary>} />
                    <Route path="/crushes" element={<ErrorBoundary><CrushesPage /></ErrorBoundary>} />
                    <Route path="/events" element={<ErrorBoundary><EventsPage /></ErrorBoundary>} />
                    <Route path="/resources" element={<ErrorBoundary><ResourcesPage /></ErrorBoundary>} />
                    <Route path="/study-buddy" element={<ErrorBoundary><StudyBuddyPage /></ErrorBoundary>} />
                    <Route path="/admin" element={<ErrorBoundary><AdminPage /></ErrorBoundary>} />
                    <Route path="/search" element={<ErrorBoundary><SearchPage /></ErrorBoundary>} />
                    <Route path="/section" element={<ErrorBoundary><SectionPage /></ErrorBoundary>} />
                    <Route path="/terms" element={<ErrorBoundary><TermsPage /></ErrorBoundary>} />
                    <Route path="/privacy" element={<ErrorBoundary><PrivacyPage /></ErrorBoundary>} />
                    <Route path="/medical-disclaimer" element={<ErrorBoundary><MedicalDisclaimerPage /></ErrorBoundary>} />
                    <Route path="/messages" element={<ErrorBoundary><MessagesPage /></ErrorBoundary>} />
                    <Route path="/news" element={<ErrorBoundary><DailyNewsPage /></ErrorBoundary>} />
                    <Route path="/dental-clinic" element={<ErrorBoundary><DentalClinicPage /></ErrorBoundary>} />
                    <Route path="/study-hub" element={<ErrorBoundary><StudyHubPage /></ErrorBoundary>} />
                    <Route path="/about" element={<ErrorBoundary><AboutPage /></ErrorBoundary>} />
                    <Route path="/write-for-us" element={<ErrorBoundary><WriteForUsPage /></ErrorBoundary>} />
                    <Route path="/contact" element={<ErrorBoundary><ContactPage /></ErrorBoundary>} />
                  </Routes>
                  </Suspense>
                </ErrorBoundary>
              </main>
              <Footer />
              <MobileNav />
              <SynapseWidget />
            </div>
            <Toaster position="top-right" theme={theme} />
            <Analytics />
          </BrowserRouter>
        </AuthProvider>
      )}
    </ThemeWrapper>
  );
}

export default App;
